/**
 * ProposalCard — the review surface for one confirm-gated assistant action (AI Assistant Phase 2).
 *
 * An AiProposal is "the assistant proposes, a human confirms": it carries a REAL device diff built
 * server-side (never by the model) that a human must review and either CONFIRM (which pushes through
 * the same manual gateway write path a hand-edit uses) or REJECT. This card renders that one review
 * gesture for the only kind this phase ships, `gateway.config`:
 *
 *   kind + target serial · rationale · the diff (via the shared DiffView) · plan warnings · the
 *   LAN-networks before/after · an optional model/confidence badge · Confirm / Reject.
 *
 * ⚠ It renders ONLY `proposed.diff`, `proposed.warnings` and networks_before/after. Every
 * `_`-prefixed key in `proposed` is server-internal confirm-time machinery (frozen target/intent/
 * spec, plan token, freshness fingerprint, pre-change snapshot) and is NEVER read here — the lone
 * exception is `_expired`, which only distinguishes a drift-expired discard from a plain reject.
 *
 * Confirm/Reject are wrapped in RoleGate min="org_admin": the confirm floor equals the manual
 * gateway-write floor, so the assistant is never a lower-privilege side door. RoleGate is a UX
 * affordance only — the server re-checks role AND tenancy on every confirm.
 *
 * Design system: surface/ink tokens for structure; the status ramp only for operational state
 * (pending/applied/expired); brand violet only for the one affordance (Confirm). Light + dark via
 * tokens. The card is presentational — the host wires the confirm/reject mutations and passes state.
 */
import type {
  AiProposal,
  AiProposalGatewayConfig,
  ConfigActionDiffEntry,
  ConfigDiff,
  GatewayNetworkRow,
  GatewayNetworkTable,
  HealthState,
} from "@/types/domain";
import { DiffView } from "@/components/data/DiffView";
import { PlanWarnings, DeltaChip } from "@/components/gateway/GatewayChrome";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { RoleGate } from "@/components/auth/RoleGate";
import { ErrorBanner } from "@/components/ui/Page";
import { ApiError } from "@/lib/api/client";
import { cn } from "@/lib/cn";

interface ProposalCardProps {
  proposal: AiProposal;
  /** Wire the accountable confirm (pushes via the manual path). Omit to render the card read-only. */
  onConfirm?: () => void;
  /** Wire the human reject (discard). Omit to render the card read-only. */
  onReject?: () => void;
  /** A confirm or reject is in flight — disables both actions and relabels Confirm. */
  busy?: boolean;
  /**
   * The error from a FAILED confirm. A 409 DRIFT carries the now-terminal, `_expired` proposal in
   * its body, which the card promotes to the rendered proposal (so it flips to the expired state and
   * hides the live actions); any other error (403/404/409-not-pending) is surfaced as a banner.
   */
  confirmError?: unknown;
}

/** The expired/terminal proposal a 409-drift confirm returns in its body, if any. */
function driftProposal(error: unknown): AiProposal | null {
  if (error instanceof ApiError && error.status === 409) {
    const body = error.body as { proposal?: AiProposal } | null;
    if (body && body.proposal) return body.proposal;
  }
  return null;
}

/**
 * Adapt the gateway config diff (ConfigActionDiffEntry[], op added/removed/changed) to the ONE
 * canonical ConfigDiff the shared DiffView renders (op add/remove/change). Reads ONLY `proposed.diff`
 * — every `_`-prefixed key in `proposed` is ignored, as required.
 */
function toConfigDiff(proposed: AiProposalGatewayConfig): ConfigDiff {
  const entries: ConfigActionDiffEntry[] = proposed.diff ?? [];
  const count = (op: ConfigActionDiffEntry["op"]) => entries.filter((e) => e.op === op).length;
  return {
    summary: `${count("added")} added · ${count("changed")} changed · ${count("removed")} removed`,
    changes: entries.map((e) => ({
      op: e.op === "added" ? "add" : e.op === "removed" ? "remove" : "change",
      path: e.path,
      before: e.before,
      after: e.after,
    })),
  };
}

/** Status pill tone for the proposal lifecycle. The ramp is operational-state only. */
function statusPill(proposal: AiProposal, expired: boolean): { tone: HealthState | "info"; label: string } {
  if (proposal.status === "confirmed") return { tone: "healthy", label: "Applied" };
  if (proposal.status === "discarded")
    return expired ? { tone: "warn", label: "Expired" } : { tone: "unknown", label: "Rejected" };
  return { tone: "info", label: "Pending review" };
}

export function ProposalCard({ proposal, onConfirm, onReject, busy, confirmError }: ProposalCardProps) {
  // A 409-drift confirm hands back the terminal, expired proposal — render THAT, not the stale one.
  const drift = driftProposal(confirmError);
  const effective = drift ?? proposal;
  const proposed = effective.proposed;
  const expired = !!proposed?._expired;
  const terminal = effective.status !== "pending" || expired;
  const pill = statusPill(effective, expired);
  // A non-drift confirm error (role/scope/not-pending) is a real failure to surface; a drift is
  // already told by the expired state, so it is not re-shown as an error banner.
  const otherError = confirmError && !drift ? confirmError : null;

  return (
    <section
      aria-label={`Assistant proposal ${effective.id}`}
      className={cn(
        "md-card overflow-hidden",
        expired && "border-warn/40",
        effective.status === "confirmed" && "border-healthy/40",
      )}
    >
      {/* Header: kind + target serial, status, and the model/confidence badge when present. */}
      <div className="flex flex-wrap items-start justify-between gap-2 border-b border-line px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6875rem] font-medium uppercase tracking-wide text-ink-muted">
              {effective.kind}
            </span>
            <span className="font-medium text-ink">{proposed?.serial ?? "—"}</span>
            <StatusPill status={pill.tone}>{pill.label}</StatusPill>
          </div>
          <div className="mt-1 text-xs text-ink-subtle">
            {proposed?.intent ? <span className="font-mono">{proposed.intent}</span> : null}
            {effective.created_by ? <span> · proposed by {effective.created_by}</span> : null}
          </div>
        </div>
        <ModelBadge model={effective.model} confidence={effective.confidence} />
      </div>

      <div className="flex flex-col gap-3 p-4">
        {/* The rationale — plain prose, stored verbatim, never executed. Model-written prose is
            labelled as such (only msp_admin+ is ever served it; AI governance P0-A). */}
        {effective.rationale && (
          <blockquote className="border-l-2 border-brand/50 bg-surface-2/50 px-3 py-2 text-sm text-ink-muted">
            {effective.rationale_source === "model" && (
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-ink-subtle">
                AI-generated
              </span>
            )}
            {effective.rationale}
          </blockquote>
        )}

        {/* The diff — rendered by the ONE shared DiffView. The card provides no confirm/discard here:
            the gated Confirm/Reject live in the footer so they can be RoleGate-wrapped. */}
        <DiffView diff={toConfigDiff(proposed)} />

        {/* Plan cautions (reused from the gateway chrome so the wording matches the manual path). */}
        <PlanWarnings warnings={proposed?.warnings ?? []} />

        {/* The LAN/VLAN table before -> after, as context for the raw diff above. */}
        {proposed?.networks_before && proposed?.networks_after && (
          <NetworksDelta before={proposed.networks_before} after={proposed.networks_after} />
        )}

        {/* Drift / terminal state, or the live confirm+reject actions. */}
        {expired ? (
          <div role="status" className="md-card border-warn/40 bg-warn-soft p-3 text-sm">
            <div className="font-medium text-ink">Expired — re-propose</div>
            <div className="mt-1 text-ink-muted">
              {proposed?._expired_reason ??
                "The live configuration changed since this proposal was created, so the reviewed diff " +
                  "no longer describes what would happen."}
            </div>
          </div>
        ) : effective.status === "confirmed" ? (
          <div role="status" className="md-card border-healthy/40 bg-healthy-soft p-3 text-sm">
            <div className="font-medium text-ink">
              Applied{effective.decided_by ? ` by ${effective.decided_by}` : ""}
            </div>
            <div className="mt-1 text-ink-muted">
              Pushed to the gateway through the standard write path and audited.
            </div>
          </div>
        ) : effective.status === "discarded" ? (
          <div role="status" className="rounded border border-line bg-surface-2/60 px-3 py-2 text-sm text-ink-muted">
            Rejected{effective.decided_by ? ` by ${effective.decided_by}` : ""} — nothing was pushed.
          </div>
        ) : null}

        {otherError != null && <ErrorBanner error={otherError} />}

        {/* PENDING only: the accountable confirm/reject, gated to org_admin (the manual-write floor). */}
        {!terminal && (onConfirm || onReject) && (
          <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
            {onReject && (
              <RoleGate min="org_admin" mode="disable" reason="Customer admin required to reject a proposal.">
                <Button variant="ghost" onClick={onReject} disabled={busy}>
                  Reject
                </Button>
              </RoleGate>
            )}
            {onConfirm && (
              <RoleGate
                min="org_admin"
                mode="disable"
                reason="Confirming an assistant proposal pushes to the gateway and requires a customer admin."
              >
                <Button variant="primary" onClick={onConfirm} disabled={busy}>
                  {busy ? "Confirming…" : "Confirm & apply"}
                </Button>
              </RoleGate>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/** The model/confidence chip — shown only when the proposal carries either. Brand-neutral (a
 *  provenance label, not an operational state), so it uses surface tokens, not the status ramp. */
function ModelBadge({ model, confidence }: { model: string | null; confidence: number | null }) {
  if (!model && confidence == null) return null;
  const pct = confidence == null ? null : `${Math.round(confidence * 100)}%`;
  return (
    <span className="shrink-0 rounded-full border border-line bg-surface-2 px-2 py-0.5 text-[0.6875rem] text-ink-muted">
      {model ?? "model"}
      {pct ? <span className="ml-1 font-mono tabular-nums text-ink-subtle">{pct}</span> : null}
    </span>
  );
}

/** The LAN/VLAN table before -> after, collapsed. Shows only the rows that changed (keyed by
 *  interface name, falling back to VLAN id), mirroring the manual screens' row-level deltas. For a
 *  change that does not touch the networks table (e.g. a DHCP-only edit) this reads "no changes". */
function NetworksDelta({ before, after }: { before: GatewayNetworkTable; after: GatewayNetworkTable }) {
  const key = (r: GatewayNetworkRow) => r.name ?? `vlan-${r.vlan ?? "?"}`;
  const beforeBy = new Map(before.networks.map((r) => [key(r), r]));
  const afterBy = new Map(after.networks.map((r) => [key(r), r]));
  const names = Array.from(new Set([...beforeBy.keys(), ...afterBy.keys()]));

  const line = (r?: GatewayNetworkRow) => {
    if (!r) return "—";
    const vlan = r.vlan != null ? `VLAN ${r.vlan}` : "untagged";
    return [vlan, r.subnet ?? "no subnet", r.interface_ip ?? ""].filter(Boolean).join(" · ");
  };

  const changed = names
    .map((n) => ({ n, b: beforeBy.get(n), a: afterBy.get(n) }))
    .filter(({ b, a }) => line(b) !== line(a));

  return (
    <details className="md-card overflow-hidden">
      <summary className="cursor-pointer px-4 py-2 text-sm text-ink-muted">
        LAN networks ({changed.length} change{changed.length === 1 ? "" : "s"}) ·{" "}
        {before.networks.length} → {after.networks.length} network
        {after.networks.length === 1 ? "" : "s"}
      </summary>
      <ul className="divide-y divide-line">
        {changed.length === 0 ? (
          <li className="px-4 py-3 text-xs text-ink-muted">
            No change to the LAN/VLAN table — see the configuration diff above.
          </li>
        ) : (
          changed.map(({ n, b, a }) => (
            <li key={`net-${n}`} className="px-4 py-2 text-xs">
              <div className="font-medium text-ink">
                {a?.name ?? b?.name ?? n}
                <DeltaChip kind={!b ? "added" : !a ? "removed" : "changed"} />
              </div>
              {b && <div className="font-mono text-ink-subtle">before: {line(b)}</div>}
              {a && <div className="font-mono text-ink-muted">after: {line(a)}</div>}
            </li>
          ))
        )}
      </ul>
    </details>
  );
}
